import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { goTo } from "@client/lib/navigation";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import { type HttpApiResponse } from "@client/types/Api";
import appInfo from "@common/appInfo";
import toasts from "@common/lib/toast";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { sortTickets } from "./lib";
import { localStateSelector } from "./selectors";
import { type MemberHelpdesk } from "./types";

export enum Actions {
  CreateCommentBegin = "MEMBER_HELPDESK_CREATE_COMMENT_BEGIN",
  CreateCommentFailure = "MEMBER_HELPDESK_CREATE_COMMENT_FAILURE",
  CreateCommentSuccess = "MEMBER_HELPDESK_CREATE_COMMENT_SUCCESS",

  CreateTicketBegin = "MEMBER_HELPDESK_CREATE_TICKET_BEGIN",
  CreateTicketFailure = "MEMBER_HELPDESK_CREATE_TICKET_FAILURE",
  CreateTicketSuccess = "MEMBER_HELPDESK_CREATE_TICKET_SUCCESS",

  CloseTicketBegin = "MEMBER_HELPDESK_CLOSE_TICKET_BEGIN",
  CloseTicketFailure = "MEMBER_HELPDESK_CLOSE_TICKET_FAILURE",
  CloseTicketSuccess = "MEMBER_HELPDESK_CLOSE_TICKET_SUCCESS",

  CreateUserFeedbackBegin = "MEMBER_HELPDESK_CREATE_USER_FEEDBACK_BEGIN",
  CreateUserFeedbackFailure = "MEMBER_HELPDESK_CREATE_USER_FEEDBACK_FAILURE",
  CreateUserFeedbackSuccess = "MEMBER_HELPDESK_CREATE_USER_FEEDBACK_SUCCESS",

  InitBegin = "MEMBER_HELPDESK_INIT_BEGIN",
  InitFailure = "MEMBER_HELPDESK_INIT_FAILURE",
  InitSuccess = "MEMBER_HELPDESK_INIT_SUCCESS",

  GetDetailBegin = "MEMBER_HELPDESK_GET_DETAIL_BEGIN",
  GetDetailFailure = "MEMBER_HELPDESK_GET_DETAIL_FAILURE",
  GetDetailSuccess = "MEMBER_HELPDESK_GET_DETAIL_SUCCESS",

  GetTicketsBegin = "MEMBER_HELPDESK_GET_TICKETS_BEGIN",
  GetTicketsFailure = "MEMBER_HELPDESK_GET_TICKETS_FAILURE",
  GetTicketsSuccess = "MEMBER_HELPDESK_GET_TICKETS_SUCCESS",

  SetShowingDetailId = "MEMBER_HELPDESK_SET_SHOWING_DETAIL_ID",

  SetAddModalVisible = "MEMBER_HELPDESK_SHOW_EDIT_MODAL",

  UpdateUserBegin = "MEMBER_HELPDESK_UPDATE_USER_BEGIN",
  UpdateUserFailure = "MEMBER_HELPDESK_UPDATE_USER_FAILURE",
  UpdateUserSuccess = "MEMBER_HELPDESK_UPDATE_USER_SUCCESS",

  UpdateViolationBegin = "MEMBER_HELPDESK_UPDATE_VIOLATION_BEGIN",
  UpdateViolationFailure = "MEMBER_HELPDESK_UPDATE_VIOLATION_FAILURE",
  UpdateViolationSuccess = "MEMBER_HELPDESK_UPDATE_VIOLATION_SUCCESS"
}

export type InitBeginActionData = EntityRef;
export interface InitSuccessActionData {
  canLeaveFeedback: boolean;
  guidelines: MemberHelpdesk.Api.Guideline[] | null;
  guidelinesVideoUrl: string | null;
  guidelineViolations: MemberHelpdesk.Api.GuidelineViolation[] | null;
  surveyPageUrl: string;
  tickets: MemberHelpdesk.Ticket[];
}
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  MemberHelpdesk.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return appInfo.user
      ? api.init(apiInfo, { size: 20000 })
      : Promise.resolve<HttpApiResponse<MemberHelpdesk.Api.InitResponse>>({
          status: 200,
          response: {
            status: "success",
            data: {
              canLeaveFeedback: false,
              surveyPageUrl: "",
              tickets: [],
              guidelinesVideoUrl: null,
              guidelines: null,
              guidelineViolations: [],
              displayHelpdeskGuidelines: false
            }
          }
        });
  },
  mapResponse: (state: BaseAppState, resp: MemberHelpdesk.Api.InitResponse): InitSuccessActionData => ({
    ...resp,
    tickets: sortTickets(resp.tickets)
  })
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type GetDetailBeginActionData = EntityRef & WithItemId;
export interface GetDetailSuccessActionData {
  detail: MemberHelpdesk.Detail;
}
export const _getDetail = apiAction<
  BaseAppState,
  GetDetailBeginActionData,
  MemberHelpdesk.Api.GetDetailResponse,
  GetDetailSuccessActionData
>({
  types: {
    begin: Actions.GetDetailBegin,
    failure: Actions.GetDetailFailure,
    success: Actions.GetDetailSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: GetDetailBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.detail(apiInfo, { ticketId: params.ticketId });
  },
  mapResponse: (state: BaseAppState, resp: MemberHelpdesk.Api.GetDetailResponse): GetDetailSuccessActionData => ({
    detail: resp
  })
});

export type WithItemId = {
  ticketId: string;
};
export type CreateCommentBeginActionData = EntityRef &
  WithItemId & {
    data: MemberHelpdesk.View.NewComment;
  };
export type CreateCommentSuccessActionData = MemberHelpdesk.Detail & {};
export const _createComment = apiAction<
  BaseAppState,
  CreateCommentBeginActionData,
  MemberHelpdesk.Api.CreateCommentResponse,
  CreateCommentSuccessActionData
>({
  types: {
    begin: Actions.CreateCommentBegin,
    failure: Actions.CreateCommentFailure,
    success: [
      Actions.CreateCommentSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CreateCommentBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);
    return api.createComment(apiInfo, {
      ...params.data,
      ticketId: localState.showingDetailId!
    });
  }
});

export const createComment =
  (ref: EntityRef, data: MemberHelpdesk.View.NewComment) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Commenting") {
      dispatch(_createComment({ ...ref, data, ticketId: localState.showingDetailId! }));
    }
  };

export type CloseTicketBeginActionData = EntityRef & WithItemId & {};

export type CloseTicketSuccessActionData = { ticketDetail: MemberHelpdesk.Api.TicketDetail };

export const _closeTicket = apiAction<
  BaseAppState,
  CloseTicketBeginActionData,
  MemberHelpdesk.Api.CloseTicketResponse,
  CloseTicketSuccessActionData
>({
  types: {
    begin: Actions.CloseTicketBegin,
    failure: Actions.CloseTicketFailure,
    success: [
      Actions.CloseTicketSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CloseTicketBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);

    return api.closeTicket(apiInfo, {
      ticketId: localState.showingDetailId!
    });
  }
});

export const closeTicket = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  if (localState.status !== "Commenting") {
    dispatch(_closeTicket({ ...ref, ticketId: localState.showingDetailId! }));
  }
};

export type RateBeginActionData = EntityRef &
  WithItemId & {
    rating: number;
    ticketFeedbackQuestionId: string;
    permalink: string | null;
  };

export type RateSuccessActionData = {
  userTicketFeedbacks: MemberHelpdesk.Api.UserTicketFeedback[];
};

export const _rate = apiAction<
  BaseAppState,
  RateBeginActionData,
  MemberHelpdesk.Api.CreateUserFeedbackResponse,
  RateSuccessActionData
>({
  types: {
    begin: Actions.CreateUserFeedbackBegin,
    failure: Actions.CreateUserFeedbackFailure,
    success: [
      Actions.CreateUserFeedbackSuccess,
      ({ params }) => {
        toasts.saved();

        params.permalink && goTo(params.permalink);
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: RateBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);

    return api.createUserFeedback(apiInfo, {
      ticketId: localState.showingDetailId!,
      rating: params.rating,
      ticketFeedbackQuestionId: params.ticketFeedbackQuestionId
    });
  }
});

export const rate =
  (ref: EntityRef, rating: number, ticketFeedbackQuestionId: string, permalink: string | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Commenting") {
      dispatch(
        _rate({ ...ref, ticketId: localState.showingDetailId!, rating, ticketFeedbackQuestionId, permalink: permalink })
      );
    }
  };

export type CreateTicketBeginActionData = EntityRef & {
  data: MemberHelpdesk.View.NewTicket;
  customFieldIds: string[];
};
export type CreateTicketSuccessActionData = MemberHelpdesk.Api.CreateTicketResponse;
export const _createTicket = apiAction<
  BaseAppState,
  CreateTicketBeginActionData,
  MemberHelpdesk.Api.CreateTicketResponse,
  CreateTicketSuccessActionData
>({
  types: {
    begin: Actions.CreateTicketBegin,
    failure: Actions.CreateTicketFailure,
    success: [
      Actions.CreateTicketSuccess,
      ({ dispatch, params }) => dispatch(hideAddModal(params)),
      () => {
        toasts.submitted();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CreateTicketBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.createTicket(apiInfo, {
      ...params.data,
      ticketProfiles: params.customFieldIds.map((x) => ({ formFieldId: x, value: params.data[x] }))
    });
  }
});

export const createTicket =
  (ref: EntityRef, data: MemberHelpdesk.View.NewTicket, customFieldIds: string[]) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Creating") {
      dispatch(_createTicket({ ...ref, data, customFieldIds }));
    }
  };

export type GetDetailArgs = EntityRef & {
  ticketId: string;
};
export const getDetail = (args: GetDetailArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (!(args.ticketId in localState.detail)) {
    dispatch(_getDetail(args));
  }
};

export type SetShowingDetailIdActionData = EntityRef & {
  ticketId: string | null;
};
export const setShowingDetailId = (args: SetShowingDetailIdActionData) => (dispatch: Dispatch<any>) => {
  if (args.ticketId) dispatch(getDetail({ id: args.id, source: args.source, ticketId: args.ticketId }));

  dispatch({
    type: Actions.SetShowingDetailId,
    data: <SetShowingDetailIdActionData>args
  });
};

export type SetAddModalVisibleActionData = EntityRef & {
  isVisible: boolean;
};
export const hideAddModal = (ref: EntityRef) => ({
  type: Actions.SetAddModalVisible,
  data: <SetAddModalVisibleActionData>{
    ...ref,
    isVisible: false
  }
});
export const showAddModal = (ref: EntityRef) => ({
  type: Actions.SetAddModalVisible,
  data: <SetAddModalVisibleActionData>{
    ...ref,
    isVisible: true
  }
});

export type UpdateUserBeginActionData = EntityRef & {};

export const _updateUser = apiAction<BaseAppState, UpdateUserBeginActionData, void>({
  types: {
    begin: Actions.UpdateUserBegin,
    failure: Actions.UpdateUserFailure,
    success: Actions.UpdateUserSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: CloseTicketBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.updateUser(apiInfo);
  }
});

export const updateUser = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  if (localState.status !== "Updating") {
    dispatch(_updateUser(ref));
  }
};

export type UpdateViolationBeginActionData = EntityRef & {
  violationId: string;
};
export type UpdateViolationSuccessActionData = {
  violation: MemberHelpdesk.Api.GuidelineViolation;
};

export const _updateViolation = apiAction<
  BaseAppState,
  UpdateViolationBeginActionData,
  MemberHelpdesk.Api.GuidelineViolation,
  UpdateViolationSuccessActionData
>({
  types: {
    begin: Actions.UpdateViolationBegin,
    failure: Actions.UpdateUserFailure,
    success: Actions.UpdateViolationSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: UpdateViolationBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.updateViolation(apiInfo, { guidelineViolationId: params.violationId });
  }
});

export const updateViolation =
  (ref: EntityRef, violationId: string) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Updating") {
      dispatch(_updateViolation({ ...ref, violationId }));
    }
  };
